import {
  type ReelTimeline,
  sourceToTimeline,
  type TimelineClip,
  timelineDuration,
  timelineToSource,
} from '@reelplicate/flows';
import { useCallback, useEffect, useRef, useState } from 'react';

/** How close to a clip's end counts as reaching it (a frame and a bit at 30 fps). */
const END_SLACK = 0.04;

const applyVolume = (video: HTMLVideoElement, clip: TimelineClip | undefined) => {
  const volume = clip?.volume ?? 1;
  video.muted = volume === 0;
  video.volume = Math.min(Math.max(volume, 0), 1);
};

/**
 * Plays an edit on the source reel: one <video> plays each clip in turn,
 * seeking to the next clip's start at each cut, so what you see is the
 * timeline, not the file. `time` is on the edited timeline.
 */
export function usePlayback(timeline: ReelTimeline) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const timelineRef = useRef(timeline);
  const clipRef = useRef(0);
  const [time, setTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const duration = timelineDuration(timeline);

  const seek = useCallback((at: number) => {
    const current = timelineRef.current;
    const clamped = Math.min(Math.max(at, 0), timelineDuration(current));
    const { index, sourceTime } = timelineToSource(current, clamped);
    clipRef.current = index;
    const video = videoRef.current;
    if (video) {
      video.currentTime = sourceTime;
      applyVolume(video, current.clips[index]);
    }
    setTime(clamped);
  }, []);

  // A changed edit keeps the playhead where it was, if it's still on the reel.
  const timeRef = useRef(time);
  useEffect(() => {
    timeRef.current = time;
  }, [time]);
  useEffect(() => {
    timelineRef.current = timeline;
    seek(timeRef.current);
  }, [timeline, seek]);

  // While playing, follow the video and jump the cuts.
  useEffect(() => {
    if (!playing) return;
    let frame = 0;
    const tick = () => {
      const video = videoRef.current;
      const current = timelineRef.current;
      if (!video) return;
      let index = clipRef.current;
      const clip = current.clips[index];
      if (!clip || video.currentTime >= clip.out - END_SLACK || video.ended) {
        index += 1;
        const next = current.clips[index];
        if (!next) {
          video.pause();
          setPlaying(false);
          setTime(timelineDuration(current));
          return;
        }
        clipRef.current = index;
        if (Math.abs(video.currentTime - next.in) > END_SLACK)
          video.currentTime = next.in;
        applyVolume(video, next);
      } else if (video.currentTime < clip.in - END_SLACK) {
        video.currentTime = clip.in;
      }
      setTime(sourceToTimeline(current, index, video.currentTime));
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [playing]);

  const pause = useCallback(() => {
    videoRef.current?.pause();
    setPlaying(false);
  }, []);

  const play = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return;
    // From the start again once it has played through.
    if (time >= timelineDuration(timelineRef.current) - END_SLACK) seek(0);
    try {
      await video.play();
      setPlaying(true);
    } catch {
      // Autoplay refused (no tap yet) or the file is gone: stay paused.
      setPlaying(false);
    }
  }, [seek, time]);

  const toggle = useCallback(() => {
    if (playing) pause();
    else void play();
  }, [pause, play, playing]);

  return { videoRef, time, duration, playing, play, pause, toggle, seek };
}

export type Playback = ReturnType<typeof usePlayback>;
